// TelcoTrack — Typ OK (počet vláken optického kabelu) pro záfuk.

export const BUDOVA_TYP_OK_VALUES = ['2 vl.', '6 vl.', '12 vl.', '24 vl.'] as const;
export const ROZVADEC_TYP_OK_VALUES = [
  '12 vl.',
  '24 vl.',
  '48 vl.',
  '96 vl.',
  '144 vl.',
  '192 vl.',
] as const;

export const TYP_OK_VALUES = BUDOVA_TYP_OK_VALUES;
/** Catalog fiber counts used as Typ OK when derived from material (not a picker). */
export const OK_CATALOG_EXTRA_TYP_OK_VALUES = ['1 vl.', '72 vl.', '288 vl.'] as const;

export const ALL_TYP_OK_VALUES = [
  ...BUDOVA_TYP_OK_VALUES,
  ...ROZVADEC_TYP_OK_VALUES,
  ...OK_CATALOG_EXTRA_TYP_OK_VALUES,
] as const;
export type TypOkValue = (typeof TYP_OK_VALUES)[number];
export type RozvadecTypOkValue = (typeof ROZVADEC_TYP_OK_VALUES)[number];

/** Polygon `constructionType === 'FTTH FIS'` → FIS metodika; ostatní → non-FIS. */
export function isPolygonFis(constructionType: string | null | undefined): boolean {
  return (constructionType ?? '').trim() === 'FTTH FIS';
}

export function isTypOkValue(value: string | null | undefined): value is TypOkValue {
  return value != null && (BUDOVA_TYP_OK_VALUES as readonly string[]).includes(value.trim());
}

export function isAnyTypOkValue(value: string | null | undefined): value is TypOkValue | RozvadecTypOkValue {
  return value != null && (ALL_TYP_OK_VALUES as readonly string[]).includes(value.trim());
}

/**
 * Canonical fiber count for an approved Typ OK label.
 * Lookup only — never parse free-form strings such as `12vl` or `12 vláken`.
 */
export const TYP_OK_FIBER_COUNT: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(
    ALL_TYP_OK_VALUES.map((label) => [label, Number.parseInt(label, 10)]),
  ),
);

export function fiberCountFromTypOk(typOk: string | null | undefined): number | null {
  const raw = (typOk ?? '').trim();
  if (!isAnyTypOkValue(raw)) return null;
  const n = TYP_OK_FIBER_COUNT[raw];
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * Canonical capacity check. Missing / unknown typOk is never unlimited — it is invalid.
 * Callers must not parse free-form `(number) vl` strings as a fallback.
 */
export function isFiberNumberWithinApprovedTypOkCapacity(
  fiberNumber: number | null | undefined,
  typOk: string | null | undefined,
): boolean {
  const capacity = fiberCountFromTypOk(typOk);
  if (capacity == null) return false;
  return (
    typeof fiberNumber === 'number' &&
    Number.isInteger(fiberNumber) &&
    fiberNumber >= 1 &&
    fiberNumber <= capacity
  );
}

/**
 * Metodika předvyplnění Typ OK podle počtu HP a FIS/non-FIS polygonu.
 * Vrací `null` pro neplatný HP (< 1).
 */
export function resolveTypOkByHp(hpCount: number, isFis: boolean): TypOkValue | null {
  const hp = Math.trunc(hpCount);
  if (!Number.isFinite(hp) || hp < 1) return null;

  if (isFis) {
    if (hp === 1) return '2 vl.';
    if (hp >= 2 && hp <= 3) return '6 vl.';
    if (hp >= 4 && hp <= 6) return '12 vl.';
    if (hp >= 7 && hp <= 8) return '24 vl.';
    // Spec: >9 (10+). HP 9 není explicitně uvedeno — padáme do 12 vl. jako nejbližší vyšší pásmo.
    if (hp >= 9) return '12 vl.';
    return null;
  }

  if (hp === 1) return '2 vl.';
  if (hp >= 2 && hp <= 3) return '6 vl.';
  if (hp >= 4 && hp <= 8) return '12 vl.';
  if (hp >= 9 && hp <= 26) return '6 vl.';
  if (hp === 27) return '6 vl.';
  if (hp >= 28) return '12 vl.';
  return null;
}
